<template>
  <div>
    <div class="text-xl mb-2">{{ $t('features.long_term.step.title') }}</div>
    <div class="text-muted">
      {{
        $t('features.long_term.step.description', {
          meter_count: meterCount,
          peak_watt_sum: sumPeakWatt,
        })
      }}
    </div>

    <Divider />

    <div
      v-for="ltmProposal in ltmProposals"
      :key="ltmProposal.title"
      class="flex flex-row my-2"
    >
      <RadioButton
        v-model="selectedProposalTitle"
        :value="ltmProposal.title"
        class="self-center mr-3"
      />

      <Message :closable="false">
        <div class="text-xl">
          {{ titleForProposal(ltmProposal) }}
        </div>
        {{ ltmProposal.description }}
      </Message>
    </div>

    <div class="flex flex-row">
      <RadioButton
        v-model="selectedProposalTitle"
        :value="customProposalTitle"
        class="self-center mr-3"
      />

      <Message :closable="false">
        <div class="text-xl">{{ customProposalTitle }}</div>
        {{ customProposalDescription }}
        <div class="flex align-items-center">
          <InputGroup class="mt-2" style="width: 16rem">
            <InputNumber
              v-model="customEnergy"
              :disabled="selectedProposalTitle !== customProposalTitle"
            />
            <InputGroupAddon>{{ energyUnit }}</InputGroupAddon>
          </InputGroup>
        </div>
      </Message>
    </div>

    <Divider />

    <div class="flex float-right">
      <Button
        class="mr-3"
        severity="secondary"
        @click="$emit('step-completed')"
      >
        {{ $t('global.skip') }}
      </Button>

      <Button
        @click="saveAndContinue"
        :disabled="saveDisabled"
        variant="primary"
        :loading="saveOngoing"
      >
        {{ $t('global.save_continue') }}
      </Button>
    </div>
  </div>
</template>
<script lang="ts">
import PlantApi from '@/data/plants/PlantApi.ts';
import PrognosisApi from '@/data/long-term/PrognosisApi.ts';
import YieldPromiseStatusApi from '@/data/yields/YieldPromiseStatusApi.ts';
import Company from '@/domain/model/business/Company.ts';
import Meter from '@/domain/model/business/Meter.ts';
import Plant from '@/domain/model/business/Plant.ts';
import Energy from '@/domain/model/unit/Energy.ts';

export default {
  name: 'LongTermStep',
  props: {
    plant: { type: Plant, required: true },
    company: { type: Company, required: true },
  },
  emits: ['step-completed'],
  methods: {
    titleForProposal(ltmProposal): string {
      return (
        ltmProposal.title +
        ': ' +
        ltmProposal.energy.asStringOfUnit(this.energyUnit) +
        ' ' +
        this.energyUnit
      );
    },
    saveAndContinue() {
      this.saveOngoing = true;

      const newPromiseTotal =
        this.selectedProposalTitle === this.proposalOptimistic.title
          ? this.proposalOptimistic.energy
          : this.selectedProposalTitle === this.proposalConservative.title
            ? this.proposalConservative.energy
            : Energy.of(this.customEnergy, this.energyUnit);

      new PlantApi()
        .getPromiseStatuses(this.company.getId(), this.plant.getId())
        .then((rsp) => {
          const ypsUpdatePromises = rsp.data.map((p) =>
            new YieldPromiseStatusApi()
              .setMinimumDurationMsForCalls(500)
              .updateYieldPromise(
                this.company.getId(),
                this.plant.getId(),
                p.id,
                { promised_total_wh: newPromiseTotal.asWh() },
              ),
          );
          Promise.all(ypsUpdatePromises) //
            .then(() => this.$emit('step-completed'))
            .finally(() => (this.saveOngoing = false));
        });
    },
  },
  computed: {
    saveDisabled() {
      const proposalTitleEqualsCustomAndInvalid =
        this.selectedProposalTitle === this.customProposalTitle &&
        (this.customEnergy === null || this.customEnergy <= 0);

      return (
        this.saveOngoing ||
        this.selectedProposalTitle === null ||
        proposalTitleEqualsCustomAndInvalid
      );
    },
    meterCount() {
      return this.meters ? this.meters.length : null;
    },
    sumPeakWatt() {
      return this.meters
        ? this.meters //
            .map((m) => m.getPeakInWatt()) //
            .reduce((a, b) => a + b, 0)
        : 0;
    },
    energyUnit() {
      return Energy.ofWh(999 * this.sumPeakWatt).getFormattedUnit();
    },
    proposalConservative() {
      return {
        title: this.$t('features.long_term.step.conservative_title'),
        description: this.$t(
          'features.long_term.step.conservative_description',
        ),
        energy: Energy.zero(),
      };
    },
    proposalOptimistic() {
      return {
        title: this.$t('features.long_term.step.optimistic_title'),
        description: this.$t('features.long_term.step.optimistic_description'),
        energy: Energy.zero(),
      };
    },
    customProposalTitle() {
      return this.$t('features.long_term.step.custom_title');
    },
    customProposalDescription() {
      return this.$t('features.long_term.step.custom_description');
    },
  },
  data() {
    return {
      saveOngoing: false,
      meters: null,
      selectedProposalTitle: null as string,
      ltmProposals: [],
      customEnergy: null,
    };
  },
  mounted() {
    new PlantApi()
      .getSinglePlantMeters(this.company.getId(), this.plant.getId())
      .then(
        (rsp) =>
          (this.meters = rsp.data
            .map((mJson) => new Meter(mJson))
            .filter((meter) => !meter.isRetired())),
      )
      .then((meters) => {
        const promises = meters.map((meter) =>
          new PrognosisApi()
            .yearlyPrognosis(this.plant, meter)
            .then((rsp) => rsp.data.data),
        );
        Promise.all(promises) //
          .then((results) => {
            this.proposalConservative.energy = Energy.ofWh(
              results.map((prog) => prog.p90_wh).reduce((a, b) => a + b, 0),
            );
            this.proposalOptimistic.energy = Energy.ofWh(
              results.map((prog) => prog.p50_wh).reduce((a, b) => a + b, 0),
            );

            this.ltmProposals = [
              this.proposalConservative,
              this.proposalOptimistic,
            ];
          });
      });
  },
};
</script>

<style scoped></style>
